/**
 * Presenting one provisioning projection. Pure — no React, no fetching.
 *
 * WHY A SHARED PRESENTER. The tenant list row, the detail header, the Overview
 * panel and the Provisioning tab each phrased provisioning for themselves.
 * Measured live on tenant 6b87bb18: the header read PROVISIONING while the
 * Provisioning tab on the same screen read Ready. Both were honest and they
 * read different fields.
 *
 * The backend now returns ONE projection (`readiness.progress`, built by
 * `provisioning_lifecycle.build_progress`). This is the only place its numbers
 * become words, so a surface cannot quietly invent a different sentence.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO: replace the lifecycle status badge.
 * `tenant.status` also carries SUSPENDED, READ_ONLY and TERMINATED, which no
 * provisioning projection knows about — and if the two ever disagree again,
 * a header that showed the projection would HIDE the divergence rather than
 * report it. Lifecycle owns the badge; this owns the progress beside it.
 */

import { describeStage } from './provisioningStages';

/** Nothing measured yet — say so, rather than showing a confident zero. */
export const NOT_MEASURED = 'Not measured';

/** No history to estimate from. NOT a number, and not zero. */
export const ESTIMATING = 'Estimating…';

/**
 * `14 of 23 steps · 61%`, or null when there is nothing honest to say.
 *
 * A tenant whose report has not arrived has no denominator, and `0 of 0 · 0%`
 * reads as "nothing has happened" for a tenant that may be nearly done.
 */
export const formatSteps = (progress) => {
  if (!progress || !progress.total_required_steps) return null;
  const { completed_required_steps: done, total_required_steps: total, percent } = progress;
  const steps = `${done} of ${total} steps`;
  return percent === null || percent === undefined ? steps : `${steps} · ${percent}%`;
};

/** `4m 12s` — elapsed since the JOB started, not since the page loaded. */
export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || seconds < 0) return null;
  const s = Math.floor(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

/**
 * Remaining time, or the honest admission that we do not know.
 *
 * `eta_seconds: null` means the backend had fewer than ETA_MIN_SAMPLES finished
 * jobs to reason from. It renders as "Estimating…" and never as a number: an
 * operator told "2 minutes" who waits ten stops believing every other figure on
 * the screen, and there is nothing to un-learn that with.
 */
export const formatEta = (progress) => {
  if (!progress || progress.state !== 'PROVISIONING') return null;
  const eta = progress.eta_seconds;
  if (eta === null || eta === undefined) return ESTIMATING;
  if (eta <= 0) return 'Finishing up';
  return `about ${formatDuration(eta)} left`;
};

/**
 * The one sentence every surface shows for a provisioning tenant.
 *
 * Returns null when the tenant is not provisioning — the caller then shows its
 * ordinary lifecycle status and nothing else, rather than a progress line that
 * has stopped meaning anything.
 */
export const describeProgress = (progress) => {
  if (!progress || progress.state !== 'PROVISIONING') return null;
  const parts = [];
  const steps = formatSteps(progress);
  parts.push(steps || NOT_MEASURED);
  // THE STAGE, IN WORDS. This pushed `progress.current_step` raw, so the
  // sentence read "18 of 20 steps · 90% · activity_tracking_defaults · 59s
  // elapsed" — a backend identifier in the middle of an operator's sentence.
  // `provisioningStages` is the only place a slug becomes words; an
  // unrecognised slug still appears there (humanised), so a newer Platform
  // cannot make this sentence go quiet about a stage it positively reported.
  const stage = describeStage(progress.current_step);
  if (stage) parts.push(stage.label);
  const elapsed = formatDuration(progress.elapsed_seconds);
  if (elapsed) parts.push(`${elapsed} elapsed`);
  const eta = formatEta(progress);
  if (eta) parts.push(eta);
  return parts.join(' · ');
};

/** Does this tenant want an operator's attention? */
export const needsAttention = (progress) => Boolean(progress && progress.needs_attention);

/**
 * DRIFT — the lifecycle badge and the provisioning evidence disagree.
 *
 * `describeProgress` says nothing outside PROVISIONING, deliberately: a stale
 * progress line beside a green badge is its own contradiction. That leaves a
 * gap, and the gap is where the real defect lives. A tenant whose provisioning
 * completed but was never promoted keeps a spinning "Provisioning" badge with
 * nothing beside it to say the work already finished; a tenant that reached
 * ACTIVE over an unbuilt baseline shows a green one just as quietly.
 *
 * The backend composes the two answers it already derives and names the
 * contradiction (`readiness.drift.code`). This turns that name into the
 * sentence, and nothing else — the codes are the backend's vocabulary and this
 * must not invent a sixth one.
 */
export const DRIFT_ACTIVE_NOT_PROVISIONED = 'active_not_provisioned';
export const DRIFT_STUCK_PROVISIONING = 'stuck_provisioning';

const DRIFT_SENTENCES = {
  [DRIFT_ACTIVE_NOT_PROVISIONED]:
    'Setup incomplete — this tenant is Active, but its runtime environment is '
    + 'not verified as built. Check the Provisioning tab before handing it over.',
  [DRIFT_STUCK_PROVISIONING]:
    'Provisioning has finished, but this tenant was never activated. '
    + 'Check the Provisioning tab and run Repair to complete the handover.',
};

/**
 * A GENERIC SENTENCE FOR AN UNRECOGNISED CODE, not silence.
 *
 * The backend can name a drift this build has never heard of — that is what
 * deploying the two halves separately means. Returning null there would show an
 * operator nothing at all for a contradiction the server had positively
 * detected, which is the exact failure this whole feature exists to end.
 */
const DRIFT_FALLBACK =
  'This tenant’s status does not match its measured provisioning state. '
  + 'Check the Provisioning tab.';

/** The warning sentence, or null when the two answers agree. */
export const describeDrift = (drift) => {
  if (!drift || !drift.code) return null;
  return DRIFT_SENTENCES[drift.code] || DRIFT_FALLBACK;
};

/**
 * The compact form, for a tenant-list row.
 *
 *     Provisioning · 61%
 *     Provisioning · Estimating…      (nothing persisted yet)
 *
 * SAME PAYLOAD, same rules, different length — a list row cannot afford the
 * full sentence and must not therefore grow its own semantics. Returns null
 * for a tenant that is not provisioning, so an ACTIVE row shows its badge and
 * nothing else rather than a progress line that stopped meaning anything.
 */
export const describeProgressCompact = (progress) => {
  if (!progress || progress.state !== 'PROVISIONING') return null;
  const { percent } = progress;
  // Unmeasured is "Estimating…", never "0%". Zero reads as "nothing has
  // happened" for a tenant that may be nearly done.
  if (percent === null || percent === undefined) return `Provisioning · ${ESTIMATING}`;
  return `Provisioning · ${percent}%`;
};
